import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import Card, { CardProps } from "@/components/wpl/card/src";
import { Picture, PictureProps } from "@/components/wpl/image/src";
import Link from "@/components/wpl/link/src";
import {
    ProductKeyTypes,
    Row,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

import { WithRequired } from "../../../../utils/typescript-utils";
interface ContentList {
    headerText: string;
    headerType?: string;
    bodyText: string;
}

interface LinkProps {
    linkText: string;
    link: string;
    linkLabel: string;
    productKey: ProductKeyTypes;
}

interface TabContentProps {
    contentList: ContentList[];
    contentLink?: LinkProps;
    pictureProps: PictureProps;
    tabContentEyebrowText?: string;
    cardsContent: WithRequired<CardProps, "clickableCardUrl">[];
    className?: string;
}

const TabsContent = (tabsContentProps: TabContentProps) => {
    return (
        <TypographyStyles>
            <div
                css={wrapperStyles}
                className={tabsContentProps.className || ""}
            >
                <div css={customGridLayoutStyles}>
                    <Row layout="4-8">
                        <div>
                            <div
                                css={contentListStyles}
                                className="content-list"
                            >
                                {tabsContentProps.contentList.map(
                                    (content, index) => (
                                        <div
                                            key={"content-" + index}
                                            css={ContentTextStyles}
                                            className="content-text"
                                        >
                                            <h3 className={content?.headerType}>
                                                {content.headerText}
                                            </h3>
                                            <p>{content.bodyText}</p>
                                        </div>
                                    )
                                )}
                            </div>
                            {tabsContentProps.contentLink && (
                                <div
                                    css={linkContentContainerStyles}
                                    className="link-content-container"
                                >
                                    <Link
                                        href={tabsContentProps.contentLink.link}
                                        text={
                                            tabsContentProps.contentLink
                                                ?.linkText
                                        }
                                        type={"basic"}
                                        doesArrowAnimate={true}
                                        dataEventProps={{
                                            "data-product-key":
                                                tabsContentProps.contentLink
                                                    ?.productKey,
                                            "data-event": "clicked",
                                            "data-event-component":
                                                "linkButton",
                                            "data-event-container":
                                                "tabsSection",
                                            "data-label-english":
                                                tabsContentProps.contentLink
                                                    ?.linkLabel
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div css={bigImgWrapperStyles}>
                            <Picture
                                {...tabsContentProps.pictureProps}
                                css={contentImageStyle}
                            />
                        </div>
                    </Row>
                </div>
                <Row>
                    {tabsContentProps.tabContentEyebrowText && (
                        <div className="tab-content-header-wrapper">
                            <h4
                                className="eyebrow-md"
                                css={css({ textAlign: "center" })}
                            >
                                {tabsContentProps.tabContentEyebrowText}
                            </h4>
                        </div>
                    )}
                </Row>
                <div className="tabs-content-cards">
                    <Row layout="4-4-4">
                        {tabsContentProps.cardsContent.map(
                            (cardContent, index) => (
                                <div
                                    key={"card-" + index}
                                    css={ContentResourcesCardStyles}
                                >
                                    <Card
                                        className={"tabs-content-card"}
                                        {...cardContent}
                                    />
                                </div>
                            )
                        )}
                    </Row>
                </div>
            </div>
        </TypographyStyles>
    );
};

const wrapperStyles = css({
    "@keyframes slidein": {
        "0%": {
            opacity: 0,
            transform: "translateX(-100px)"
        },
        "100%": {
            opacity: 1,
            transform: "translateX(0)"
        }
    },
    "animation": `slidein linear 500ms 1`
});

const bigImgWrapperStyles = css({
    overflow: "hidden",
    img: {
        animation: `slidein linear 500ms 1`
    }
});

const linkContentContainerStyles = css({
    marginBlockStart: "24px",
    fontSize: "1rem",
    lineHeight: "1.5rem",
    fontWeight: 600
});

const contentListStyles = css({
    [media.only.sm]: {
        display: "grid",
        gap: "32px"
    }
});

const customGridLayoutStyles = css({
    [media.below.sm]: {
        ".row-layout": {
            padding: "0px"
        },
        ".row-layout > div": {
            padding: "0px 32px"
        }
    }
});

const contentImageStyle = css({
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
    alignSelf: "baseline",
    height: "auto",

    [media.below.sm]: {
        marginBlockStart: "6px"
    }
});

const ContentResourcesCardStyles = css({
    ".tabs-content-card": {
        "borderRadius": "10px",
        "overflow": "hidden",
        "boxShadow": "none",
        "border": "1px solid #B7B9BE",
        "cursor": "pointer",
        "transition": "box-shadow 0.2s ease-in-out",

        ":hover": {
            boxShadow: "0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F"
        }
    },
    ".text-section": {
        "height": "100%",
        "lineHeight": "1.4",
        "h5": {
            fontSize: "1.25rem",
            fontWeight: 700,
            lineHeight: "1.625rem",
            color: "#101214",
            height: "100%",

            [media.only.sm]: {
                fontSize: "1.375rem",
                lineHeight: "1.75rem"
            },
            [media.above.md]: {
                fontSize: "1.5rem",
                lineHeight: "1.875rem"
            }
        },
        ".contentBlock-linkFlex": {
            marginBlockEnd: "0px",
            fontSize: "1rem",
            lineHeight: "1.5rem",
            fontWeight: 600
        },
        ".contentBlock-eyebrow": {
            marginBlockEnd: "8px",
            fontWeight: 400,
            fontSize: "0.75rem",
            lineHeight: "1rem",
            textTransform: "uppercase"
        }
    }
});

const ContentTextStyles = css({
    [media.above.md]: {
        "paddingBlockEnd": "8px",
        "borderBlockEnd": "1.5px dashed #B3BAC5",
        "&:only-of-type": {
            borderBlockEnd: "none"
        }
    }
});

export default TabsContent;
